import React from "react";
import { commitMutation, graphql } from "react-relay";

import cx from "classnames";

import { showFeatureModal, UpgradeIcon } from "~/billing";
import { useID } from "~/lib/hooks";
import { promptHeadingsEnabled } from "~/lib/prose/plugins/prompt";
import { environment } from "~/models";
import { ButtonLink, HelpCenterIcon, Switch } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/LoadingSpinner";
import toast from "~/ui/toast";

import { AutoShareRecapSettingsConfig } from "../AutoShareRecapSettings";
import AutoShareToChannelsAutomation, {
	ConfigurationPageRenderer as AutoShareToChannelsConfiguration,
} from "./AutoShareToChannels";
import MeetingRecaps from "./MeetingRecaps";
import MeetingReminders from "./MeetingReminders";
import { PromptNotifications } from "./PromptNotification";
import { StreamAutoRecordAutomationConfig } from "./StreamAutoRecordAutomationConfig";
import TimeSaver from "./TimeSaver";
import {
	AUTOMATION_CODE,
	type AutomationType,
	type ConfigurationType,
	type StreamSettingsViewer,
	type UserType,
} from "./types";

import type { AutomationsToggleConfigurationMutation } from "~/__generated__/AutomationsToggleConfigurationMutation.graphql";

import stylesSettings from "~/settings/css/settings.module.css";
import stylesStreamSettings from "~/stream/StreamSettings/StreamSettings.module.css";

import styles from "./Automations.module.css";

graphql`
	fragment AutomationsConfigurationsData on AutomationConfigurationNode {
		id
		isActive

		automation {
			codeName
		}

		stream {
			id
			hasAnyAutomationActive
		}

		tasks {
			__typename
			id
			delta

			... on TimeSaverReminderAutomationTask {
				data {
					type
					addBanner
					sendNotificationTo
				}
			}

			... on TimeSaverCancellationAutomationTask {
				data {
					type
					sendNotificationTo
				}
			}

			... on EmailReminderAutomationTask {
				data {
					notificationPath
					message
					allParticipants
					userIds
				}
			}

			... on SlackReminderAutomationTask {
				data {
					notificationPath
					message
					slackTeamId
					channelId
					channelName
				}
			}

			... on MSTeamsReminderAutomationTask {
				data {
					notificationPath
					message
					team
					teamName
					conversation
					conversationName
					isUser
				}
			}

			... on ZoomReminderAutomationTask {
				data {
					notificationPath
					message
					zoomChannelId
					zoomChannelName
				}
			}

			... on EmailRecapAutomationTask {
				data {
					notificationPath
					message
					allParticipants
					emails
					includeAiSummary
				}
			}

			... on SlackRecapAutomationTask {
				data {
					notificationPath
					message
					slackTeamId
					channelId
					channelName
					includeAiSummary
				}
			}

			... on MSTeamsRecapAutomationTask {
				data {
					notificationPath
					message
					team
					teamName
					conversation
					conversationName
					isUser
					includeAiSummary
				}
			}

			... on ZoomRecapAutomationTask {
				data {
					notificationPath
					message
					zoomChannelId
					zoomChannelName
					includeAiSummary
				}
			}

			... on PromptNotificationAutomationTask {
				data {
					message
				}
			}
		}
	}
`;

const toggleAutomationConfigurationMutation = graphql`
	mutation AutomationsToggleConfigurationMutation($configurationId: ID!) {
		toggleAutomationConfiguration(configurationId: $configurationId) {
			configuration {
				id
				isActive

				stream {
					id
					hasAnyAutomationActive
					schedulingMeetingAssistantAutomationIsActive
				}
			}
		}
	}
`;

interface AutomationItemProps {
	viewer: StreamSettingsViewer;
	automation: AutomationType;
	config?: ConfigurationType;
	onChange: () => void;
	onConfigure?: () => void;
}

function TimeSaverAutomation({ viewer, automation, config, onChange, onConfigure }: AutomationItemProps) {
	const toggleID = useID();
	const paywalled = !viewer.account.billingAccount.hasAccessToMeetingAutomations;

	const showPaywall = React.useCallback(() => {
		showFeatureModal("meeting_automations");
	}, []);

	return (
		<section className={styles.automationBlock}>
			<div className={styles.row}>
				<h1 className={styles.title}>
					{automation.name}
					<HelpCenterIcon
						articleId="5650922"
						title="Set up no agenda reminder & cancellation automation"
						className={styles.headerHelpIcon}
					/>
				</h1>
				<ButtonLink onClick={paywalled ? showPaywall : onConfigure} className={styles.configureLink}>
					Configure
				</ButtonLink>
			</div>
			<div className={styles.row} onClick={paywalled ? showPaywall : undefined}>
				<label htmlFor={toggleID} className={styles.label}>
					{automation.description}
				</label>
				{paywalled && <UpgradeIcon />}
				<Switch
					id={toggleID}
					checked={!!config?.isActive}
					onCheckedChange={onChange}
					data-disabled={paywalled ?? undefined}
					className={cx(stylesStreamSettings.toggle, stylesSettings.settingsPageToggle)}
				/>
			</div>
		</section>
	);
}

function MeetingReminderRecapAutomation({ viewer, automation, config, onChange, onConfigure }: AutomationItemProps) {
	const toggleID = useID();
	const tasksCount = config?.tasks.length ?? 0;
	const paywalled = !viewer.account.billingAccount.hasAccessToMeetingAutomations;

	const showPaywall = React.useCallback(() => {
		showFeatureModal("meeting_automations");
	}, []);

	return (
		<section className={styles.automationBlock}>
			<div className={styles.row}>
				<h1 className={styles.title}>
					{automation.name}
					{!config?.isActive && !paywalled && tasksCount > 0 && <b className={styles.numTasks}>{tasksCount}</b>}
				</h1>
				<ButtonLink onClick={paywalled ? showPaywall : onConfigure} className={styles.configureLink}>
					Configure
				</ButtonLink>
			</div>
			<div className={styles.row} onClick={paywalled ? showPaywall : undefined}>
				<label htmlFor={toggleID} className={styles.label}>
					{automation.description}
				</label>
				{paywalled && <UpgradeIcon />}
				<Switch
					id={toggleID}
					checked={!!config?.isActive}
					onCheckedChange={onChange}
					data-disabled={paywalled ?? undefined}
					className={cx(stylesStreamSettings.toggle, stylesSettings.settingsPageToggle)}
				/>
			</div>
		</section>
	);
}

function PromptAutomation({ viewer, automation, config, onChange, onConfigure }: AutomationItemProps) {
	const toggleID = useID();
	const tasksCount = config?.tasks.length ?? 0;
	const paywalled = !viewer.account.billingAccount.hasAccessToPromptHeadings;

	const showPaywall = React.useCallback(() => {
		showFeatureModal("prompt_headings");
	}, []);

	if (!promptHeadingsEnabled) return null;

	return (
		<section className={styles.automationBlock}>
			<div className={styles.row}>
				<h1 className={styles.title}>
					{automation.name}
					<HelpCenterIcon articleId="5992965" title="Sections overview" className={styles.headerHelpIcon} />
					{!config?.isActive && !paywalled && tasksCount > 0 && <b className={styles.numTasks}>{tasksCount}</b>}
				</h1>
				<ButtonLink onClick={paywalled ? showPaywall : onConfigure} className={styles.configureLink}>
					Configure
				</ButtonLink>
			</div>
			<div className={styles.row} onClick={paywalled ? showPaywall : undefined}>
				<label htmlFor={toggleID} className={styles.label}>
					{automation.description}
				</label>
				{paywalled && <UpgradeIcon />}
				<Switch
					id={toggleID}
					checked={!!config?.isActive}
					onCheckedChange={onChange}
					data-disabled={paywalled ?? undefined}
					className={cx(stylesStreamSettings.toggle, stylesSettings.settingsPageToggle)}
				/>
			</div>
		</section>
	);
}

type AutomationProps = {
	automation: AutomationType;
	config: ConfigurationType | undefined;
	viewer: StreamSettingsViewer;
	onConfigure: (codeName: string) => void;
	streamId: string;
};

export function Automation({ automation, config, onConfigure: _onConfigure, viewer, streamId }: AutomationProps) {
	const onConfigure = React.useCallback(() => _onConfigure(automation.codeName), [_onConfigure, automation.codeName]);
	const onChange = React.useCallback(() => {
		if (config?.id) {
			commitMutation<AutomationsToggleConfigurationMutation>(environment, {
				mutation: toggleAutomationConfigurationMutation,
				variables: { configurationId: config?.id },
				onCompleted: ({ toggleAutomationConfiguration }, err) => {
					if (err?.length || !toggleAutomationConfiguration) {
						toast.error(err?.[0]?.message || "Failed to save settings");
					}
				},
			});
		} else {
			_onConfigure(automation.codeName);
		}
	}, [_onConfigure, config?.id, automation.codeName]);

	const isTimeSaverAutomation = automation.codeName === AUTOMATION_CODE.TIME_SAVER;
	const isMeetingReminderAutomation = automation.codeName === AUTOMATION_CODE.MEETING_REMINDER;
	const isMeetingRecapAutomation = automation.codeName === AUTOMATION_CODE.MEETING_RECAP;
	const isPromptAutomation = automation.codeName === AUTOMATION_CODE.PROMPT_NOTIFICATION;
	const isSchedulingAutomation = automation.codeName === AUTOMATION_CODE.SCHEDULING_MEETING_ASSISTANT;
	const isAutoShareToChannelsAutomation = automation.codeName === AUTOMATION_CODE.AUTO_SHARE_TO_CHANNELS;

	const props = { viewer, automation, config, onChange, onConfigure };

	if (isTimeSaverAutomation) {
		return <TimeSaverAutomation {...props} />;
	} else if (isMeetingReminderAutomation || isMeetingRecapAutomation) {
		return <MeetingReminderRecapAutomation {...props} />;
	} else if (isPromptAutomation) {
		return <PromptAutomation {...props} />;
	} else if (isSchedulingAutomation) {
		return (
			<React.Suspense fallback={<LoadingSpinner />}>
				<StreamAutoRecordAutomationConfig streamID={streamId} viewer={props.viewer} />
			</React.Suspense>
		);
	} else if (isAutoShareToChannelsAutomation) {
		const isPaywalled = !viewer.account.billingAccount.hasAccessToMeetingAutomations;
		return <AutoShareToChannelsAutomation streamId={streamId} onConfigure={onConfigure} isPaywalled={isPaywalled} />;
	}

	return null;
}

interface ConfigPageProps {
	streamId: string;
	automation: AutomationType | { id: "fake"; codeName: "MEETING_RECAP_AUTO_SHARE" };
	usersWithAccess: UserType[];
	viewerHasOffice: boolean;
	viewerHasSlack: boolean;
	viewerHasZoom: boolean;
	onBack: () => void;
	configuration?: ConfigurationType;
}

export function ConfigPage({
	streamId,
	automation,
	onBack,
	configuration,
	usersWithAccess,
	viewerHasOffice,
	viewerHasSlack,
	viewerHasZoom,
}: ConfigPageProps) {
	const code = automation.codeName as AUTOMATION_CODE | "MEETING_RECAP_AUTO_SHARE";

	const commonProps = { streamId, automationId: automation.id, onBack };

	if (code === AUTOMATION_CODE.TIME_SAVER) {
		return (
			<section>
				<TimeSaver {...commonProps} tasks={configuration?.tasks} />
			</section>
		);
	} else if (code === AUTOMATION_CODE.MEETING_REMINDER) {
		return (
			<section>
				<MeetingReminders
					{...commonProps}
					tasks={configuration?.tasks}
					usersWithAccess={usersWithAccess}
					viewerHasOffice={viewerHasOffice}
					viewerHasSlack={viewerHasSlack}
					viewerHasZoom={viewerHasZoom}
				/>
			</section>
		);
	} else if (code === AUTOMATION_CODE.MEETING_RECAP) {
		return (
			<section>
				<MeetingRecaps
					{...commonProps}
					tasks={configuration?.tasks}
					viewerHasOffice={viewerHasOffice}
					viewerHasSlack={viewerHasSlack}
					viewerHasZoom={viewerHasZoom}
				/>
			</section>
		);
	} else if (code === AUTOMATION_CODE.PROMPT_NOTIFICATION) {
		// If the configuration didn't already exist, clicking "configure" will run
		// a mutation that also fetches the configurations again. This spinner will
		// show up in the meantime.
		if (!configuration) return <LoadingSpinner delay={500} dimmer={false} />;
		return (
			<section>
				<PromptNotifications {...commonProps} tasks={configuration?.tasks} />
			</section>
		);
	} else if (code === "MEETING_RECAP_AUTO_SHARE") {
		return <AutoShareRecapSettingsConfig streamId={streamId} onBack={onBack} />;
	} else if (code === AUTOMATION_CODE.AUTO_SHARE_TO_CHANNELS) {
		return (
			<section>
				<AutoShareToChannelsConfiguration {...commonProps} />
			</section>
		);
	}

	// code no supported
	return null;
}
